import React from 'react';
import { CaretRight, CheckCircle, Eye } from '@phosphor-icons/react';

/**
 * WhatMattersMost — Wave 4 Phase 1C
 *
 * Compact hero that anchors every report detail page. Phase 1B established
 * the structure. Phase 1C softens the language and adjusts visual rhythm:
 *   - "WHAT MATTERS MOST" eyebrow stays — it's the founder's frame
 *   - "Kindra remembered" → "Now part of your record" (less AI-engine-ish)
 *   - "Awaiting your confirmation" → "Help Kindra confirm these" (collaborative)
 *   - "Kindra will now watch for" → "Kindra will keep an eye on" (quieter)
 *   - "Worth raising" → "Worth raising with your doctor" (specific)
 *   - More breathing room: space-y-4 → space-y-5; outer padding p-5/p-6 → p-6/p-7
 *   - Subsection headers slightly less aggressive (tracking + weight)
 *
 * No new sections, no new chips, no new AI features — only tone + rhythm.
 */
export const WhatMattersMost = ({ extraction = {}, pendingCount = 0, autoSavedCount = 0, confirmedCount = 0 }) => {
  const summary = (extraction.summary || extraction.plain_language || '').trim();

  const rememberedLabels = [
    ...(extraction.confirmed_facts || []),
    ...(extraction.auto_saved_facts || extraction.auto_saved || []),
  ]
    .map((f) => (f && f.label ? f.label : null))
    .filter(Boolean)
    .slice(0, 4);

  const watchLabels = (extraction.things_to_track || [])
    .map((t) => (typeof t === 'string' ? t : t?.label || t?.name || ''))
    .filter(Boolean)
    .slice(0, 3);

  const topQuestions = (extraction.suggested_questions || []).slice(0, 3);

  if (!summary && rememberedLabels.length === 0 && watchLabels.length === 0 && topQuestions.length === 0) {
    return null;
  }

  return (
    <section
      className="card-base p-6 sm:p-7 border-l-4 border-l-[#235347]"
      data-testid="what-matters-most"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#235347] mb-4">
        What matters most
      </p>

      {summary && (
        <p
          className="text-[15.5px] sm:text-[16px] text-[#0E1C2E] leading-[1.6] mb-6 font-serif italic"
          data-testid="wmm-summary"
        >
          {summary.length > 320 ? `${summary.slice(0, 317).trimEnd()}…` : summary}
        </p>
      )}

      <div className="space-y-5">
        {/* Now part of your record */}
        {(rememberedLabels.length > 0 || autoSavedCount + confirmedCount > 0) && (
          <div data-testid="wmm-remembered">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 mb-2.5 flex items-center gap-1.5">
              <CheckCircle size={11} weight="fill" className="text-[#2FB3A0]" />
              Now part of your record
            </h3>
            {rememberedLabels.length > 0 ? (
              <ul className="space-y-1.5">
                {rememberedLabels.map((label, idx) => (
                  <li
                    key={idx}
                    className="text-[13.5px] text-slate-700 flex items-start gap-2 leading-snug"
                  >
                    <span className="text-[#2FB3A0] mt-0.5">•</span>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[12.5px] italic text-slate-500 leading-snug">
                {autoSavedCount + confirmedCount} item{(autoSavedCount + confirmedCount) === 1 ? '' : 's'} added — Kindra will reach for these in future conversations.
              </p>
            )}
          </div>
        )}

        {/* Help Kindra confirm */}
        {pendingCount > 0 && (
          <div data-testid="wmm-pending">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 mb-1.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Help Kindra confirm these
            </h3>
            <p className="text-[12.5px] text-slate-600 leading-snug">
              {pendingCount} item{pendingCount === 1 ? '' : 's'} below — tap to keep or skip.
            </p>
          </div>
        )}

        {/* Kindra will keep an eye on */}
        {watchLabels.length > 0 && (
          <div data-testid="wmm-watch">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 mb-2.5 flex items-center gap-1.5">
              <Eye size={11} weight="fill" className="text-slate-400" />
              Kindra will keep an eye on
            </h3>
            <ul className="space-y-1.5">
              {watchLabels.map((label, idx) => (
                <li key={idx} className="text-[13.5px] text-slate-700 flex items-start gap-2 leading-snug">
                  <span className="text-slate-400 mt-0.5">•</span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Worth raising with your doctor */}
        {topQuestions.length > 0 && (
          <div data-testid="wmm-questions">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 mb-2.5 flex items-center gap-1.5">
              <CaretRight size={11} weight="fill" className="text-[#235347]" />
              Worth raising with your doctor
            </h3>
            <ul className="space-y-2">
              {topQuestions.map((q, idx) => (
                <li key={idx} className="text-[13.5px] text-slate-700 leading-relaxed">
                  <span className="text-slate-300 mr-1.5">{idx + 1}.</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default WhatMattersMost;
